/* eslint-disable @nx/enforce-module-boundaries */
// Home.js (main component)
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Skeleton } from '@mui/material';

import ArrowRight from '../../assets/SVGComponents/iconArrowRight';

import ReadingModeContainer from '../components/reading-areas/ReadingModeContainer';
import PreviewModeContainer from '../components/reading-areas/PreviewModeContainer';
import SectionStatistics from '../components/core/SectionStatistics';
import { useStoreByName } from '../../hooks/use-stores.hooks';
import { useBookContent } from '../../hooks/useBooksQuery';
import { useReadingOnboardingTour } from '../../hooks/use-onboarding-tour.hooks';
import Legend from '../components/core/legend';

type ContentLevel = {
  one?: undefined | string;
  two?: undefined | string;
  three?: undefined | string;
  four?: undefined | string;
  five?: undefined | string;
  six?: undefined | string;
  /** Torah only: parasha bookmark on the current chapter */
  parasha?: undefined | string;
};

interface ReadingStats {
  read_count: number;
  high_score: number;
  last_score: number;
  average_score?: number;
}

// פונקציה גנרית לשליפת סטטיסטיקות קטע
function getSectionStats(
  scoreInfo: any[],
  bookNameEn: string,
  subCategoryNameHe: string,
  topSectionNameHe: string,
  sectionName: string | number
) {
  // console.log('🔍 getSectionStats - התחלה:', {
  //   scoreInfo: scoreInfo ? `Array of ${scoreInfo.length} books` : 'null',
  //   bookNameEn,
  //   subCategoryNameHe,
  //   topSectionNameHe,
  //   sectionName,
  // });

  if (!scoreInfo) {
    // console.log('❌ getSectionStats - scoreInfo is null');
    return null;
  }

  // שלב 1: מציאת הספר (עם תיקון עבור ktuvim/ketuvim)
  let searchBookName = bookNameEn.toLowerCase();
  if (searchBookName === 'ketuvim') {
    searchBookName = 'ktuvim'; // תיקון עבור באג השרת
  }

  const book = scoreInfo.find((b) => b.book_name_en.toLowerCase() === searchBookName);
  // console.log('📖 שלב 1 - חיפוש ספר:', {
  //   searched: searchBookName,
  //   original: bookNameEn.toLowerCase(),
  //   availableBooks: scoreInfo.map((b) => b.book_name_en),
  //   found: !!book,
  // });

  if (!book) {
    // console.log('❌ ספר לא נמצא');
    return null;
  }

  // שלב 2: מציאת תת-הקטגוריה
  const subCategory = book.book_statistics?.sub_category_array?.find(
    (sc: any) => sc.sub_category_name_he === subCategoryNameHe
  );
  // console.log('📂 שלב 2 - חיפוש תת-קטגוריה:', {
  //   searched: subCategoryNameHe,
  //   availableSubCategories: book.book_statistics?.sub_category_array?.map((sc: any) => sc.sub_category_name_he) || [],
  //   found: !!subCategory,
  // });

  if (!subCategory) {
    // console.log('❌ תת-קטגוריה לא נמצאה');
    return null;
  }

  // שלב 3: מציאת החלק העליון
  const topSection = subCategory.top_section_array?.find((ts: any) => ts.top_section_name_he === topSectionNameHe);
  // console.log('📄 שלב 3 - חיפוש חלק עליון:', {
  //   searched: topSectionNameHe,
  //   availableTopSections: subCategory.top_section_array?.map((ts: any) => ts.top_section_name_he) || [],
  //   found: !!topSection,
  // });

  if (!topSection) {
    // console.log('❌ חלק עליון לא נמצא');
    return null;
  }

  // שלב 4: מציאת הקטע - חיפוש גם לפי שם וגם לפי מספר
  const section = topSection.section_array?.find(
    (s: any) => s.section_name === sectionName || s.section_name == sectionName
  );
  // console.log('📋 שלב 4 - חיפוש קטע:', {
  //   searched: sectionName,
  //   availableSections: topSection.section_array?.map((s: any) => s.section_name) || [],
  //   found: !!section,
  // });

  if (!section) {
    // console.log('❌ קטע לא נמצא');
    return null;
  }

  // שלב 5: החזרת הסטטיסטיקות
  const result = {
    in_writing: section.in_writing,
    by_heart: section.by_heart,
  };
  // console.log('✅ getSectionStats - הצלחה:', result);
  return result;
}

function Reading() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPreviewContent, setShowPreviewContent] = useState(true);
  const [showReadingContent, setShowReadingContent] = useState(false);
  const [showByHeartContent, setShowByHeartContent] = useState(false);
  const [contentLevels, setContentLevels] = useState<ContentLevel>({});
  const [readingStats, setReadingStats] = useState<ReadingStats | null>(null);
  const [byHeartStats, setByHeartStats] = useState<ReadingStats | null>(null);
  const [isHeadersLoading, setIsHeadersLoading] = useState(true);

  const { selectedBook, selectedLevelOne, selectedLevelTwo, selectedLevelThree, selectedLevelFour, selectedLevelFive, currentParasha } =
    useStoreByName('booksStore');
  const { userData } = useStoreByName('userStore');
  const { prepareToListen, releaseMicrophone } = useStoreByName('speechRecognitionStore');
  const { t } = useTranslation();

  // While the verse is on screen, open the microphone and the recogniser ahead
  // of the green button, so pressing it finds them ready. Nothing is sent until
  // the reading starts, and the controller closes both after 30 seconds unused.
  useEffect(() => {
    if (!showPreviewContent) return;
    prepareToListen?.().catch((error: unknown) => {
      console.warn('Could not prepare the microphone:', error);
    });
  }, [showPreviewContent, prepareToListen]);

  // Leaving the page closes whatever was opened ahead of time.
  useEffect(() => {
    return () => {
      releaseMicrophone?.().catch((error: unknown) => {
        console.warn('Could not release the microphone:', error);
      });
    };
  }, [releaseMicrophone]);
  
  // TanStack Query hook for book content - handles caching automatically
  // This ensures data is loaded when navigating to reading page
  useBookContent(selectedBook?.name_en || '');

  // סיור הדרכה חד-פעמי שמסביר את שני מצבי התרגול, אחרי שהכותרות נטענו
  useReadingOnboardingTour(showPreviewContent && !isHeadersLoading);

  const startLearningMode = (mode: string) => {
    setShowPreviewContent(false);
    switch (mode) {
      case 'reading':
        setShowReadingContent(true);
        break;
      case 'byHeart':
        setShowByHeartContent(true);
        break;
    }
  };

  const endHandler = (mode: string) => {
    setTimeout(() => {
      setShowPreviewContent(true);
      switch (mode) {
        case 'reading':
          setShowReadingContent(false);
          break;
        case 'byHeart':
          setShowByHeartContent(false);
          break;
      }
    }, 500);
  };

  const backHandler = () => {
    // Check if we came from personal area via the state
    if (location.state?.fromPersonalArea) {
      navigate('/personal-area');
    } else {
      navigate('/home');
    }
  };

  /**
   * אפקט זה אחראי על:
   * 1. בדיקת תקינות הניווט - אם אין ספר נבחר או רמה ראשונה, המשתמש יוחזר לדף הבית
   * 2. הגדרת מבנה התוכן בהתאם לסוג הספר הנבחר (משניות/תורה או סידור)
   * 3. ארגון הרמות השונות של התוכן בצורה היררכית
   */
  useEffect(() => {
    setIsHeadersLoading(true);
    const timeout = setTimeout(() => {
      setIsHeadersLoading(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!selectedLevelOne || !selectedBook) {
      backHandler();
    }

    // בודק שכל הנתונים הנדרשים זמינים לפני הצגת הכותרות
    if (selectedLevelOne?.label) {
      const isMishna = selectedBook?.name_en?.toLowerCase() === 'mishna';
      const newContentLevels: ContentLevel = {
        one: selectedLevelOne?.label || '',
        two: selectedLevelTwo?.label || '',
        three: selectedLevelThree?.label || '',
        four: isMishna ? '' : selectedLevelFour?.label || '',
        five: selectedLevelFive?.label || '', // הוספת רמה חמישית לתת-נושאים בסידור
        parasha: currentParasha?.name || '', // תורה: הפרשה שהפסוק הנוכחי נמצא בה
      };

      // בודק שיש שינוי אמיתי בכותרות לפני העדכון
      if (JSON.stringify(newContentLevels) !== JSON.stringify(contentLevels)) {
        setContentLevels(newContentLevels);
      }
      // אין צורך ב-setTimeout כאן, כי הטעינה הראשונית כבר טופלה באפקט הראשון
      setIsHeadersLoading(false);
    }
  }, [selectedLevelOne?.label, selectedLevelTwo?.label, selectedLevelThree?.label, selectedLevelFour?.label, selectedLevelFive?.label, currentParasha?.name]);

  /**
   * אפקט זה אחראי על:
   * 1. טיפול ספציפי במקרה של לימוד משניות
   * 2. חיפוש והצגת הסטטיסטיקות של המשתמש עבור החלק הנבחר
   * 3. הפרדה בין סטטיסטיקות קריאה לסטטיסטיקות שינון בעל-פה
   */
  useEffect(() => {
    // console.log('🔄 useEffect לסטטיסטיקות - התחלה:', {
    //   selectedBook: selectedBook?.name,
    //   selectedLevelOne: selectedLevelOne?.label,
    //   selectedLevelTwo: selectedLevelTwo?.label,
    //   selectedLevelThree: selectedLevelThree?.label,
    //   hasUserData: !!userData,
    //   hasScoreInfo: !!userData?.scoreInfo,
    //   scoreInfoLength: userData?.scoreInfo?.length || 0,
    // });

    // בדיקת תקינות נתונים - למשנה יש 3 רמות, לתורה/נביאים/כתובים יש 4
    const isMishna = selectedBook?.name_en?.toLowerCase() === 'mishna';

    if (
      !selectedBook ||
      !selectedLevelOne ||
      !selectedLevelTwo ||
      !selectedLevelThree ||
      (!isMishna && !selectedLevelFour) ||
      !userData?.scoreInfo
    ) {
      // איפוס הסטטיסטיקות אם אין נתונים
      setReadingStats(null);
      setByHeartStats(null);
      return;
    }

    const bookNameEn = selectedBook.name_en;
    let subCategoryNameHe = '';
    let topSectionNameHe = '';
    let sectionName = '';

    if (isMishna) {
      // משנה: רמה 1 → 2 → 3
      subCategoryNameHe = selectedLevelOne?.label || '';
      topSectionNameHe = selectedLevelTwo?.label || '';
      sectionName = selectedLevelThree?.label || '';
    } else {
      // תורה/נביאים/כתובים: רמה 2 → 3 → 4 (להתעלם מרמה 1)
      subCategoryNameHe = selectedLevelTwo?.label || '';
      topSectionNameHe = selectedLevelThree?.label || '';
      sectionName = selectedLevelFour?.label || '';
    }

    // console.log('📝 נתונים לפני התאמה:', {
    //   bookNameEn,
    //   subCategoryNameHe,
    //   topSectionNameHe,
    //   sectionName,
    //   isMishna
    // });

    // התאמת קידומות
    if (isMishna) {
      // הוספת קידומת "משנה " ל-sectionName אם חסרה
      if (!sectionName.includes('משנה')) {
        sectionName = 'משנה ' + sectionName;
      }

      // console.log('📝 נתונים אחרי התאמה למשנה:', {
      //   bookNameEn,
      //   subCategoryNameHe,
      //   topSectionNameHe,
      //   sectionName,
      // });
    } else {
      // בדיקה מיוחדת עבור תהלים
      if (subCategoryNameHe.includes('תהלים')) {
        // החלפת "פרק " ב"מזמור " ב-topSectionNameHe בלבד
        if (topSectionNameHe.includes('פרק ')) {
          topSectionNameHe = topSectionNameHe.replace('פרק ', 'מזמור ');
        } else if (!topSectionNameHe.includes('מזמור')) {
          topSectionNameHe = 'מזמור ' + topSectionNameHe;
        }

        // sectionName נשאר פסוק כמו בכל הספרים
        if (!sectionName.includes('פסוק')) {
          sectionName = 'פסוק ' + sectionName;
        }
      } else {
        // תורה/נביאים/כתובים אחרים: הוספת קידומת "פסוק " ל-sectionName אם חסרה
        if (!sectionName.includes('פסוק')) {
          sectionName = 'פסוק ' + sectionName;
        }
      }
    }

    const stats = getSectionStats(userData.scoreInfo, bookNameEn, subCategoryNameHe, topSectionNameHe, sectionName);

    // console.log('📊 תוצאות סטטיסטיקות:', {
    //   stats,
    //   readingStats: stats?.in_writing,
    //   byHeartStats: stats?.by_heart,
    // });

    // עדכון הסטטיסטיקות בstate
    const newReadingStats = stats?.in_writing || null;
    const newByHeartStats = stats?.by_heart || null;

    setReadingStats(newReadingStats);
    setByHeartStats(newByHeartStats);

    // console.log('📈 סטטיסטיקות עודכנו:', {
    //   readingStats: newReadingStats,
    //   byHeartStats: newByHeartStats,
    //   hasReadingData: !!newReadingStats && newReadingStats.read_count > 0,
    //   hasByHeartData: !!newByHeartStats && newByHeartStats.read_count > 0,
    // });
  }, [selectedBook, selectedLevelOne, selectedLevelTwo, selectedLevelThree, selectedLevelFour, userData?.scoreInfo]);

  return (
    <main className="body reading-page">
      <div className="head-section">
        <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
          <div className="navigate">
            <ArrowRight onClick={backHandler} />
          </div>
          <div className="headers">
            {isHeadersLoading ? (
              <>
                <h1 className="" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Skeleton variant="text" width={100} height={40} />
                  <span style={{ opacity: 0.7, color: 'var(--text-color, #333)' }}>|</span>
                  <Skeleton variant="text" width={100} height={40} />
                  {contentLevels?.five && (
                    <>
                      <span style={{ opacity: 0.7, color: 'var(--text-color, #333)' }}>|</span>
                      <Skeleton variant="text" width={150} height={40} />
                    </>
                  )}
                </h1>
                <h2 className="" style={{ marginTop: '8px' }}>
                  <Skeleton variant="text" width={180} height={32} />
                </h2>
                <h4 className="" style={{ marginTop: '4px' }}>
                  <Skeleton variant="text" width={160} height={24} />
                </h4>
              </>
            ) : selectedBook?.name_en === 'Torah' ? (
              /* Torah: חומש → פרק → פסוק, with the parasha shown as a bookmark */
              <>
                <h1 className="">{`${contentLevels?.two || ''} ${
                  contentLevels?.three ? '| ' + contentLevels.three : ''
                }`}</h1>
                <h2 className="">{contentLevels?.parasha ? `פרשת ${contentLevels.parasha}` : ''}</h2>
                <h4 className="">{`ספר ${contentLevels?.one || ''}`}</h4>
              </>
            ) : contentLevels?.four ? (
              <>
                <h1 className="">{`${contentLevels?.three || ''} ${
                  contentLevels?.three && contentLevels?.four ? '|' : ''
                } ${contentLevels?.four || ''} ${contentLevels?.five ? '| ' + contentLevels.five : ''}`}</h1>
                <h2 className="">
                  {selectedBook?.name_en === 'Torah' ? `פרשת ${contentLevels?.two || ''}` : contentLevels?.two || ''}
                </h2>
                <h4 className="">
                  {selectedBook?.name_en === 'Torah'
                    ? `ספר ${contentLevels?.one || ''}`
                    : contentLevels?.one?.replace('משנה', 'מסכת') || ''}
                </h4>
              </>
            ) : (
              <>
                <h1 className="">{`${contentLevels?.two || ''} ${
                  contentLevels?.three ? '| ' + contentLevels.three : ''
                }`}</h1>
                <h2 className="">{contentLevels?.one?.replace('משנה', 'מסכת') || ''}</h2>
              </>
            )}
          </div>
        </div>
        <SectionStatistics readingStats={readingStats} byHeartStats={byHeartStats} showAverageScore={false} />
      </div>
      <section>
        <div className="card">
          {showPreviewContent && (
            <PreviewModeContainer showPreviewContent={showPreviewContent} onStartReading={startLearningMode} />
          )}
          {showReadingContent && <ReadingModeContainer mode="reading" endHandler={endHandler} stats={readingStats} />}
          {showByHeartContent && <ReadingModeContainer mode="byHeart" endHandler={endHandler} stats={byHeartStats} />}
          <Legend />
        </div>
      </section>
    </main>
  );
}

export default observer(Reading);
